/**
 * Common Constants
 */

import config from "utils/appConfig";

export const USER_LOCALE = navigator.language.toLowerCase();
export const HOME_ROUTE = "/libraries/";

export const ENV = config.buildENV;
export const LS_REDIRECT = "redirect";
export const API_URL = config.apiURL;
export const UI_URL = config.uiURL;
export const D_TREE_URL = config.dtreeURL;
export const AD_STUDIO_URL = config.adStudioURL;
export const PREVIEW_2_URL = config.preview2URL;
export const TIA_DOMAIN = config.tiaDomain;
export const TIA_DOMAIN_PUBLIC = config.tiaDomainPublic;
export const MAX_SHARED_CREATIVES = config.maxSharedCreatives;
export const LOGIN_ASSETS_URL = config.loginAssetsURL;
export const IMPOSIUM_URL = config.imposiumURL;
export const CTV_PREVIEW_URL = config.ctvPreviewURL;
export const MAX_VERSION_GEN_CREATIVES = config.maxVersionGenCreatives;

export const FACEBOOK_DSP_ID = 4;	//The ID of Facebook's DSP connection within our Database
export const STATUS_BAD_REQUEST = 400;
export const STATUS_NOT_FOUND = 404;

//General Constants
export const SUPPRESS_SUCCESS_GROWL = { suppressGrowl:true };
export const SUPPRESS_ERROR_GROWL = { suppressErrorGrowl:true };
export const SUPPRESS_ALL_GROWL = { suppressGrowl: true, suppressErrorGrowl:true };

export const SEARCH_DEBOUNCE = 900;	//Debounce search every 900ms

// Moment formats for localized date/time display
export const DATE_FORMAT = "L"; // Month numeral, day of month, year, with leading zeroes
export const TIME_FORMAT = "LT"; // Localized time, without seconds (if you need seconds, use "LTS")
export const DATE_AND_TIME_FORMAT = `${DATE_FORMAT} ${TIME_FORMAT}`;

export const API_DATE_FORMAT = "YYYY-MM-DD";

// This is used to pre-populate start date filters when startDate is not defined
export const TURN_OF_THE_CENTURY = "2000-01-01";

export const DEFAULT_RPP        = 25; // Results Per Page
export const DEFAULT_ROW_HEIGHT = 35;
/* eslint-disable no-magic-numbers */

// RPP options
export const LIBRARY_ROW_COUNT_OPTIONS = [25, 50, 75, 100, 500];
export const CREATIVE_ROW_COUNT_OPTIONS = [25, 50, 75, 100, 500];
export const QA_ROW_COUNT_OPTIONS = [25, 50, 75, 100, 500];
export const DTREE_ROW_COUNT_OPTIONS = [25, 50, 75, 100, 500];
export const CONTENT_FEED_ROW_COUNT_OPTIONS = [25, 50, 75, 100, 500];

/* eslink-enable no-magic-numbers */

export const MIN_GRID_HEIGHT = 500;

export const UPLOAD_CREATIVE_ID = "UPLOAD_CREATIVE_INPUT_1";
export const UPLOAD_CREATIVE_FILE_TYPES = ".zip, .png, .jpg, .jpeg, .gif, .svg";
export const UPLOAD_VAST_CREATIVE = "UPLOAD_VAST_CREATIVE";
export const UPLOAD_VAST_ACCEPTED_FORMATS = ".mp4, .mov";
export const UPLOAD_VAST_AFTER_EFFECTS_FORMATS = ".zip";

export const UPLOAD_AUDIO_CREATIVE = "UPLOAD_AUDIO_CREATIVE";
export const UPLOAD_AUDIO_ACCEPTED_FORMATS = ".mp3, .ogg, .m4a, .aac";

export const UPLOAD_VIDEO_ASSET_MAX_BYTES = 2_000_000_000; // 2 GB
export const UPLOAD_AUDIO_ASSET_MAX_BYTES = 100_000_000; // 100 MB
export const UPLOAD_IMAGE_ASSET_MAX_BYTES = 10_000_000; // 10 MB
export const UPLOAD_RICHLOAD_ASSET_MAX_BYTES = 10_000_000; // 10 MB
export const UPLOAD_AFTER_EFFECTS_MAX_BYTES = 2_000_000_000; // 2 GB

export const ASSET_MAX_BYTES_BY_TYPE = {
	image: UPLOAD_IMAGE_ASSET_MAX_BYTES,
	video: UPLOAD_VIDEO_ASSET_MAX_BYTES,
	audio: UPLOAD_AUDIO_ASSET_MAX_BYTES,
	richload: UPLOAD_RICHLOAD_ASSET_MAX_BYTES,
};

export const IMPOSIUM_COMPOSITION_DIMENSION_OPTIONS = [
	{
		value: "1920x1080",
		label: "1920x1080 (Horizontal)",
	},
	{
		value: "1080x1920",
		label: "1080x1920 (Vertical)",
	},
	{
		value: "1080x1080",
		label: "1080x1080 (Square)",
	},
	{
		value: "custom",
		label: "Custom",
	}
];

export const IMPOSIUM_COMPOSITION_DURATION_OPTIONS = [
	{
		label: "6 seconds",
		value: 6,
	},
	{
		label: "15 seconds",
		value: 15,
	},
	{
		label: "30 seconds",
		value: 30,
	},
	{
		label: "1 minute",
		value: 60,
	},
	{
		label: "Custom duration",
		value: "custom",
	},
];

export const IMPOSIUM_COMPOSITION_FRAMERATE_OPTIONS = [
	{
		label: "23.976 fps",
		value: 23.976,
	},
	{
		label: "24 fps",
		value: 24,
	},
	{
		label: "25 fps",
		value: 25,
	},
	{
		label: "29.98 fps",
		value: 29.98,
	},
	{
		label: "30 fps",
		value: 30,
	},
	{
		label: "60 fps",
		value: 60,
	},
];


export const IMPOSIUM_COMPOSITION_CONSTRAINTS = {
	duration: {
		min: 1,
		max: 120,
	},
	dimensions: {
		min: 100,
		max: 4096,
	},
};



/**
 * Social Stuff
 */
export const SOCIAL_AD_TYPES = {
	// Flashtalking Facebook types
	FACEBOOK_IMAGE: "facebookImage",
	FACEBOOK_VIDEO: "facebookVideo",
	FACEBOOK_CAROUSEL: "facebookCarousel",

	// Mediaocean Facebook types
	FACEBOOK_IMAGE_MO: "facebookImageMO",
	FACEBOOK_VIDEO_MO: "facebookVideoMO",
	FACEBOOK_CAROUSEL_MO: "facebookCarouselMO",

	GOOGLE_DEMAND_GEN_IMAGE: "googleDiscMulti",
	GOOGLE_DEMAND_GEN_CAROUSEL: "googleDiscCarousel",

	YOUTUBE_INSTREAM: "youTubeInStream",
	YOUTUBE_DISCOVERY: "youTubeDiscovery",

	LINKEDIN_IMAGE: "linkedInImage",
	LINKEDIN_VIDEO: "linkedInVideo",
	LINKEDIN_CAROUSEL: "linkedInCarousel",

	PINTEREST_IMAGE: "pinterestImage",
	PINTEREST_VIDEO: "pinterestVideo",
	PINTEREST_CAROUSEL: "pinterestCarousel",
	PINTEREST_MAX_WIDTH_VIDEO: "pinterestMaxWVideo",

	SNAPCHAT_IMAGE: "snapImage",
	SNAPCHAT_VIDEO: "snapVideo",
	SNAPCHAT_COMMERCIAL: "snapCommercial",
	SNAPCHAT_CAROUSEL: "snapStoryCarousel",

	TWITTER_IMAGE: "twitterImage",
	TWITTER_VIDEO: "twitterVideo",
	TWITTER_CAROUSEL: "twitterCarousel",

	TIKTOK_VIDEO: "tiktokVideo",

	REDDIT_IMAGE: "redditImage",
	REDDIT_VIDEO: "redditVideo",
};
export const SOCIAL_AD_TYPE_LIST = Object.values(SOCIAL_AD_TYPES);
export const CAROUSEL_AD_TYPES = SOCIAL_AD_TYPE_LIST.filter(item => /carousel/i.test(item));
export const VIDEO_AD_TYPES = [
	SOCIAL_AD_TYPES.FACEBOOK_VIDEO,
	SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO,
	SOCIAL_AD_TYPES.YOUTUBE_INSTREAM,
	SOCIAL_AD_TYPES.YOUTUBE_DISCOVERY,
	SOCIAL_AD_TYPES.LINKEDIN_VIDEO,
	SOCIAL_AD_TYPES.PINTEREST_VIDEO,
	SOCIAL_AD_TYPES.PINTEREST_MAX_WIDTH_VIDEO,
	SOCIAL_AD_TYPES.SNAPCHAT_VIDEO,
	SOCIAL_AD_TYPES.SNAPCHAT_COMMERCIAL,
	SOCIAL_AD_TYPES.TWITTER_VIDEO,
	SOCIAL_AD_TYPES.TIKTOK_VIDEO,
];
export const DYNAMIC_SOCIAL_AD_TYPES = [
	SOCIAL_AD_TYPES.FACEBOOK_IMAGE_MO,
	SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO,
	SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO,

	SOCIAL_AD_TYPES.LINKEDIN_IMAGE,
	SOCIAL_AD_TYPES.LINKEDIN_VIDEO,
	SOCIAL_AD_TYPES.LINKEDIN_CAROUSEL,

	SOCIAL_AD_TYPES.PINTEREST_IMAGE,
	SOCIAL_AD_TYPES.PINTEREST_VIDEO,
	SOCIAL_AD_TYPES.PINTEREST_CAROUSEL,
	SOCIAL_AD_TYPES.PINTEREST_MAX_WIDTH_VIDEO,

	SOCIAL_AD_TYPES.SNAPCHAT_IMAGE,
	SOCIAL_AD_TYPES.SNAPCHAT_VIDEO,
	SOCIAL_AD_TYPES.SNAPCHAT_COMMERCIAL,
	SOCIAL_AD_TYPES.SNAPCHAT_CAROUSEL,

	SOCIAL_AD_TYPES.TIKTOK_VIDEO,

	SOCIAL_AD_TYPES.REDDIT_IMAGE,
	SOCIAL_AD_TYPES.REDDIT_VIDEO,
];

export const MAX_DYNAMIC_SOCIAL_VERSIONS = 500;

export const PLATFORMS = {
	FACEBOOK: "Meta",
	FACEBOOK_MO: "Meta Mediaocean",
	INSTAGRAM: "Instagram",
	GOOGLE: "Google Ads",
	YOUTUBE: "YouTube",
	LINKEDIN: "LinkedIn",
	PINTEREST: "Pinterest",
	SNAPCHAT: "Snapchat",
	TIKTOK: "TikTok",
	TWITTER: "X",
	REDDIT: "Reddit",
};

export const CREATIVE_ASSET_TYPES = { // AKA Build Method
	ADSTUDIO: "adstudio",
	PREBUILT: "prebuilt",
	IMPOSIUM: "imposium",
	AFTER_EFFECTS: "after_effects",
};

export const SOCIAL_FORMATS = {
	IMAGE: "image",
	VIDEO: "video",
};
export const SOCIAL_FORMAT_LIST = Object.values(SOCIAL_FORMATS);

export const META_PAC_PARTNERS = {
	INSTAGRAM: 'instagram',
	FACEBOOK: 'facebook',
};

// Generally we get adType icons from Rhombus, via FontAwesome, but we sometimes need image fallbacks
export const IMAGE_ICONS = {
	[SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_IMAGE]: "/images/GoogleDiscovery.png",
	[SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_CAROUSEL]: "/images/GoogleDiscovery.png",
};

// Various creative types
export const ALTIMAGE = "altimage";
export const DISPLAY_HTML_TAG = "display_html_tag";
export const DYNAMIC_AUDIO = "dynamic_audio";
export const DYNAMIC_PREROLL = "dynamic_preroll";
export const HTML_ONPAGE = "HTML_onpage";
export const HTML_STANDARD = "HTML_standard";
export const HTML_EXPAND = "HTML_expand";
export const JS_VPAID = "JsVPaid";
export const NATIVE_IMAGE = "native_image";
export const PARTNER_VAST = "vast_tag";
export const PREROLL = "preroll";
export const VAST_AUDIO = "vastAudio";
export const VAST_YOUTUBE = "vastYoutube";
export const CTV_CREATIVE = "CTV";

// Create Vast Dynamic Radio Field Values
export const VAST_NON_DYNAMIC = "nonDynamic";
export const VAST_DYNAMIC = "dynamic";
export const VAST_IMPOSIUM = "imposium";
export const VAST_DYNAMIC_AFTER_EFFECTS = "dynamicAfterEffects";

// Create Vast labels associated with build method
export const VAST_BUILD_METHOD_LABELS = {
	[VAST_NON_DYNAMIC]: "Standard Video",
	[VAST_DYNAMIC]: "Dynamic - Ad Studio",
	[VAST_DYNAMIC_AFTER_EFFECTS]: "Dynamic - After Effects",
	[VAST_IMPOSIUM]: "Dynamic - Imposium (Advanced Video)",
};

// Create Audio Dynamic Radio Field Values
export const AUDIO_NON_DYNAMIC = "nonDynamicAudio";
export const AUDIO_IMPOSIUM = "imposiumAudio";

// Create Audio labels associated with build method
export const AUDIO_BUILD_METHOD_LABELS = {
	[AUDIO_NON_DYNAMIC]: "Standard Audio",
	[AUDIO_IMPOSIUM]: "Dynamic Audio - Imposium",
};

export const EXISTING_CREATIVES_AD_TYPE_LIST = [
	...SOCIAL_AD_TYPE_LIST,
	NATIVE_IMAGE,
	PREROLL,
	DYNAMIC_PREROLL,
	JS_VPAID,
];

export const PREVIEW_2_ADTYPES = [
	...SOCIAL_AD_TYPE_LIST,
	PREROLL,
	// DYNAMIC_PREROLL, // Dynamic Preroll can only be viewed in PRVW if it's Approved status
	VAST_AUDIO,
	DISPLAY_HTML_TAG,
	PARTNER_VAST,
];

export const LEGACY_PREVIEW_ADTYPES = [JS_VPAID, NATIVE_IMAGE];

export const NON_AUTOCLASSIFIABLE_ADTYPES = [
	...CAROUSEL_AD_TYPES,
	SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_IMAGE,
	VAST_AUDIO,
	PARTNER_VAST,
	CTV_CREATIVE,
];

export const PLATFORM_BY_ADTYPE = {
	[SOCIAL_AD_TYPES.FACEBOOK_IMAGE]: PLATFORMS.FACEBOOK,
	[SOCIAL_AD_TYPES.FACEBOOK_VIDEO]: PLATFORMS.FACEBOOK,
	[SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL]: PLATFORMS.FACEBOOK,

	[SOCIAL_AD_TYPES.FACEBOOK_IMAGE_MO]: PLATFORMS.FACEBOOK_MO,
	[SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO]: PLATFORMS.FACEBOOK_MO,
	[SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO]: PLATFORMS.FACEBOOK_MO,

	[SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_IMAGE]: PLATFORMS.GOOGLE,
	[SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_CAROUSEL]: PLATFORMS.GOOGLE,

	[SOCIAL_AD_TYPES.YOUTUBE_INSTREAM]: PLATFORMS.YOUTUBE,
	[SOCIAL_AD_TYPES.YOUTUBE_DISCOVERY]: PLATFORMS.YOUTUBE,

	[SOCIAL_AD_TYPES.LINKEDIN_IMAGE]: PLATFORMS.LINKEDIN,
	[SOCIAL_AD_TYPES.LINKEDIN_VIDEO]: PLATFORMS.LINKEDIN,
	[SOCIAL_AD_TYPES.LINKEDIN_CAROUSEL]: PLATFORMS.LINKEDIN,

	[SOCIAL_AD_TYPES.PINTEREST_IMAGE]: PLATFORMS.PINTEREST,
	[SOCIAL_AD_TYPES.PINTEREST_VIDEO]: PLATFORMS.PINTEREST,
	[SOCIAL_AD_TYPES.PINTEREST_MAX_WIDTH_VIDEO]: PLATFORMS.PINTEREST,
	[SOCIAL_AD_TYPES.PINTEREST_CAROUSEL]: PLATFORMS.PINTEREST,

	[SOCIAL_AD_TYPES.SNAPCHAT_IMAGE]: PLATFORMS.SNAPCHAT,
	[SOCIAL_AD_TYPES.SNAPCHAT_VIDEO]: PLATFORMS.SNAPCHAT,
	[SOCIAL_AD_TYPES.SNAPCHAT_COMMERCIAL]: PLATFORMS.SNAPCHAT,
	[SOCIAL_AD_TYPES.SNAPCHAT_CAROUSEL]: PLATFORMS.SNAPCHAT,

	[SOCIAL_AD_TYPES.TWITTER_IMAGE]: PLATFORMS.TWITTER,
	[SOCIAL_AD_TYPES.TWITTER_VIDEO]: PLATFORMS.TWITTER,
	[SOCIAL_AD_TYPES.TWITTER_CAROUSEL]: PLATFORMS.TWITTER,

	[SOCIAL_AD_TYPES.TIKTOK_VIDEO]: PLATFORMS.TIKTOK,

	[SOCIAL_AD_TYPES.REDDIT_IMAGE]: PLATFORMS.REDDIT,
	[SOCIAL_AD_TYPES.REDDIT_VIDEO]: PLATFORMS.REDDIT,
};

export const FACEBOOK_DSP_INTERNAL_NAME = "facebook";
export const YOUTUBE_DSP_INTERNAL_NAME = "youtube";

export const TYPES = {
	ASSETS:    'assets',
	LIBRARIES: 'libraries',
	CREATIVES: 'creatives',
	PREVIEWS:  'creativePreviews',
	CONCEPTS:  'concepts',
	VERSIONS:  'versions',
	DTREES:    'dtrees',
};

// Add version statuses as required
export const VERSION_STATUS = {
	LIVE: 'Live',
	UNPUBLISHED: 'Unpublished',
};

export const ASSET_TYPES = {
	IMAGE: "Image",
	VIDEO: "Video",
	RICHLOAD: "Richload",
	TEXT: "Text",
	AUDIO: "Audio",
	AFTEREFFECTS:"AfterEffects",
};

export const MODAL_TYPES = {
	APPROVE_CREATIVES: "APPROVE_CREATIVES",
	ADD_CREATIVES_TO_CONCEPT: "ADD_CREATIVES_TO_CONCEPT",
	RENAME_CREATIVES: "RENAME_CREATIVES",
	RENAME_CONCEPT: "RENAME_CONCEPT",
	SUBMIT_CREATIVES: "SUBMIT_CREATIVES",
	VIEW_REJECT_REASON: "VIEW_REJECT_REASON",
	DUPLICATE_CREATIVES: "DUPLICATE_CREATIVES",
	DUPLICATE_CONCEPTS: "DUPLICATE_CONCEPTS",
	CONCEPTS: "Concepts",
	CREATE_IMPOSIUM_PROJECT: "CREATE_IMPOSIUM_PROJECT",
	ADD_CREATIVES_TO_IMPOSIUM_PROJECT: "ADD_CREATIVES_TO_IMPOSIUM_PROJECT",
	IMPOSIUM_COMPOSITIONS: "IMPOSIUM_COMPOSITIONS",
	SWITCH_IMPOSIUM_COMPOSITION: "SWITCH_IMPOSIUM_COMPOSITION",
	CREATIVES: "Creatives",
	DTREE:"DecisionTrees",
	APPROVAL_HISTORY: "ApprovalHistory",
	socialCreativeImport:"socialCreativeImport",
	socialSocketErrors:"socialSocketErrors",
	IMPORT_TAXONOMY:"ImportTaxonomy",
	PREVIEW_LINKED_CREATTIVES: "PreviewLinkedCreatives",
	ASSET_LINKED_CREATIVES: "AssetLinkedCreatives",
	ASSET_DYNAMIC_VARIABLES: "AssetDynamicVariables",
	UPLOAD_TEXT_ASSET: "UploadTextAsset",
	RECALL_CREATIVES: "RecallCreatives",
	REJECT_CREATIVES: "rejectCreatives",
	VIEW_ENCODES: "VIEW_ENCODES",
	ADD_ADSTUDIO_TO_CONCEPT: "AdstudioCreatives",
	PREVIEW_ASSET: "PreviewAsset",
	CONTENT_FEED_DECISIONING_MODAL: "ContentFeedDecisioningModal",
	DECISION_TREE_MODAL: "DecisionTreeModal",
	CONFIRM_BULK_EXPORT_VERSIONS: "ConfirmBulkExportVersions",
	IMPORT_VERSIONS_ERROR: "ImportVersionsError",
	REMOVE_CREATIVES_FROM_CONCEPT: "RemoveCreativesFromConcept",
	IMPORT_DECISION_SHEET_ERROR: "ImportDecisionSheetError",
	ADD_CREATIVE_TAGS_TO_CONCEPT: "ADD_CREATIVE_TAGS_TO_CONCEPT",
	SHARE_CREATIVE_PREVIEWS: "SHARE_CREATIVE_PREVIEWS",
	DELETE_CREATIVES_ERRORS: "DELETE_CREATIVES_ERRORS",
	DUPLICATE_QR_CODE_DESIGN: "DUPLICATE_QR_CODE_DESIGN",

	AE_NOT_ALLOWED:"AE_NOT_ALLOWED",
	AE_UPLOAD_ERRORS:"AE_UPLOAD_ERRORS",
	EDIT_PARTNER_VAST:"EDIT_PARTNER_VAST",

	ASSIGN_CONTENT_FEED: "ASSIGN_CONTENT_FEED",
	ASSIGN_EXISTING_CONTENT_FEED: "ASSIGN_EXISTING_CONTENT_FEED",
	ASSIGN_NEW_CONTENT_FEED: "ASSIGN_NEW_CONTENT_FEED",
};

export const MODAL_PATHS = {
	ASSIGN_EXISTING_CONTENT_FEED: "assign-existing-content-feed",
	ASSIGN_NEW_CONTENT_FEED: "assign-new-content-feed",
};

export const SOCIAL_PLATFORM_TYPES = {
	[SOCIAL_AD_TYPES.FACEBOOK_IMAGE]: "facebook-creative",
	[SOCIAL_AD_TYPES.FACEBOOK_VIDEO]: "facebook-creative",
	[SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL]: "facebook-creative",
	[SOCIAL_AD_TYPES.FACEBOOK_IMAGE_MO]: "facebook-creative",
	[SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO]: "facebook-creative",
	[SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO]: "facebook-creative",
	[SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_IMAGE]: "google-creative",
	[SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_CAROUSEL]: "google-creative",
	[SOCIAL_AD_TYPES.YOUTUBE_DISCOVERY]: "youtube-creative",
	[SOCIAL_AD_TYPES.YOUTUBE_INSTREAM]: "youtube-creative",
	[SOCIAL_AD_TYPES.LINKEDIN_IMAGE]: "linkedin-creative",
	[SOCIAL_AD_TYPES.LINKEDIN_VIDEO]: "linkedin-creative",
	[SOCIAL_AD_TYPES.LINKEDIN_CAROUSEL]: "linkedin-creative",
	[SOCIAL_AD_TYPES.PINTEREST_IMAGE]: "pinterest-creative",
	[SOCIAL_AD_TYPES.PINTEREST_VIDEO]: "pinterest-creative",
	[SOCIAL_AD_TYPES.PINTEREST_MAX_WIDTH_VIDEO]: "pinterest-creative",
	[SOCIAL_AD_TYPES.PINTEREST_CAROUSEL]: "pinterest-creative",
	[SOCIAL_AD_TYPES.SNAPCHAT_IMAGE]: "snapchat-creative",
	[SOCIAL_AD_TYPES.SNAPCHAT_VIDEO]: "snapchat-creative",
	[SOCIAL_AD_TYPES.SNAPCHAT_COMMERCIAL]: "snapchat-creative",
	[SOCIAL_AD_TYPES.SNAPCHAT_CAROUSEL]: "snapchat-creative",
	[SOCIAL_AD_TYPES.TWITTER_IMAGE]: "twitter-creative",
	[SOCIAL_AD_TYPES.TWITTER_VIDEO]: "twitter-creative",
	[SOCIAL_AD_TYPES.TIKTOK_VIDEO]: "tiktok-creative",
	[SOCIAL_AD_TYPES.REDDIT_IMAGE]: "reddit-creative",
	[SOCIAL_AD_TYPES.REDDIT_VIDEO]: "reddit-creative",
};

export const SOCIAL_CONCEPT_TYPE = "social";

export const ADSTUDIO_TYPE = "adstudio";

export const IMPOSIUM_TYPE = "imposium";

export const HTML_CONCEPT_TYPE = "HTML";

/**
 * Constants for different services
 */
export const JCRM_SOCKET = "JCRM";

export const IMPOSIUM_SOCIAL_RENDER_TASK = "imposium_social_render_task";

export const INCOMPLETE_STATUS = ["PRODUCTION_INCOMPLETE", "REJECTED_INCOMPLETE"];

/**
 * Decisioning
 */

// Expected values for `decisioningType` on Creatives
export const DECISIONING_TYPES = {
	DECISION_TREE: "Decision Tree",
	DECISION_SHEET: "Decision Sheet",
	WEIGHTED_ROTATION: "Weighted Rotation",
	AUTO_OPTIMIZATION: "Auto-Optimization",
	CONTENT_FEED: "Bulk Sheet",
	PUBLISHED_VERSION: "Published Version",
	NO_DECISIONING: "No Decisioning",
	UNKNOWN:"unknown",
};

// keys are expected values for decisioningName on Auto Optimized Creatives, with user friendly labels as values
export const AUTO_OPTIMIZATION_LABELS = {
	auto: "Auto-Opt (Click Through Rate)",
	"auto-acquisition": "Auto-Opt (Acquisition Rate)",
	"auto-interaction": "Auto-Opt (Interaction Rate)",
	"auto-videoPlayed": "Auto-Opt (% Video Played)",
	"auto-soundOn": "Auto-Opt (Sound On Rate)",
	modern: "Auto-Optimization (Modern)",
	statistical_significance: "Auto-Opt (Statistical Significance)",
	softmax: "Auto-Opt (New Modern)",
};

export const DECISIONING_ICON_MAP = {
	[DECISIONING_TYPES.DECISION_TREE]:{ iconName:"dtree", iconClass:"decisioning", iconStyle:"fal" },
	[DECISIONING_TYPES.DECISION_SHEET]:{ iconName:"hedge", iconClass:"decisioning", iconStyle:"far" },
	[DECISIONING_TYPES.WEIGHTED_ROTATION]:{ iconName:"rotation", iconClass:"decisioning", iconStyle:"far" },
	[DECISIONING_TYPES.AUTO_OPTIMIZATION]:{ iconName:"autoOptimization", iconClass:"decisioning", iconStyle:"far"  },
	[DECISIONING_TYPES.CONTENT_FEED]:{ iconName:"rss", iconClass:"decisioning", iconStyle:"far"  },
	[DECISIONING_TYPES.PUBLISHED_VERSION]:{ iconName:"checkMark", iconClass:"center-icon basic-check", iconStyle:"far"  },
	[DECISIONING_TYPES.NO_DECISIONING]:{ iconName:"" },
	[DECISIONING_TYPES.UNKNOWN]:{
		label:"unknown",
		iconName:"fa-thumbtack",
		iconStyle:"fas",
		labelClass:"in-unknown in-production",
	},
};

export const WEIGHT_MULTIPLIER_DECIMAL_PLACES = 2; // Api returns up to 2 decimal places for weightMultiplier field

export const TARGET_METHODS = {
	WEIGHTED_ROTATION: "Weighted Rotation",

	LEGACY: "legacy",
	MODERN: "modern",
	MODERN_AUDIENCE: "modern audience",
	STATISTICAL_SIGNIFICANCE: "statistical_significance",
	SOFTMAX: "softmax",
};

// The Attention KPI can't be used with these target methods.
export const KPI_ID_ATTENTION = 4;
export const ATTENTION_KPI_DISALLOWED_TARGET_METHODS = [TARGET_METHODS.LEGACY, TARGET_METHODS.MODERN, TARGET_METHODS.MODERN_AUDIENCE];

export function filterAdvertiserObjectivesForTargetMethod(advertiserObjectives = [], targetMethod) {
	return advertiserObjectives.filter((objective) => {
		return objective?.kpiEvent?.kpi?.id !== KPI_ID_ATTENTION || !ATTENTION_KPI_DISALLOWED_TARGET_METHODS.includes(targetMethod);
	});
}

export const LEGACY_TARGET_METHODS = {
	CLICK_THROUGH: "click through rate",
	ACQUISITION_RATE: "acquisition rate",
	INTERACTION_RATE: "interaction rate",
	PERCENTAGE_VIDEO_PLAYED: "% of video played",
	SOUND_ON_RATE: "sound on rate",
};

export const MAX_VERSIONS_BY_TARGET_METHOD = {
	[TARGET_METHODS.MODERN]:1000,
	[TARGET_METHODS.MODERN_AUDIENCE]:250,
};

export const WINNER_PERCENTAGE_MIN = 2;
export const WINNER_PERCENTAGE_MAX = 95;
export const WINNER_PERCENTAGE_DEFAULT = 75;
export const STATISTICAL_SIGNIFICANCE_MIN = 0.1;
export const STATISTICAL_SIGNIFICANCE_MAX = 99.9;
export const STATISTICAL_SIGNIFICANCE_DEFAULT = 95;
export const MIN_EVENTS_MIN = 1;
export const MIN_EVENTS_MAX = 60;
export const MIN_EVENTS_DEFAULT = 30;
export const TEMPERATURE_DEFAULT = "medium";

export const TEMPERATURE_OPTIONS = [
	{ value: "high", label: "High" },
	{ value: "medium", label: "Medium" },
	{ value: "low", label: "Low" },
];


/**
 * Constants for slicing title in YouTube Discovery Ads
 */

export const MAX_YT_TITLE_LENGTH  = 25;

export const MENU_DIVIDER = { divider:true, name:"divider" };

export const STATUS_MAP = {
	failed:{
		label:"Rejected",
		icon:"rejected",
		labelClass:"in-rejected",
	},
	submitted:{
		label:"QA",
		icon:"fas fa-map-marker-question",
		labelClass:"in-qa",
	},
	unsubmitted:{
		label:"Production",
		icon:"deleted",
		labelClass:"in-production",
	},
	pending_approval:{
		label:"Approving",
		icon:"fas fa-bullseye",
		labelClass:"in-approving",
	},
	approved:{
		label:"Approved",
		icon:"approved",
		labelClass:"in-approved",
	},
	live:{
		label:"live",
		icon:"approved",
		labelClass:"in-approved",
	},
	unknown:{
		label:"unknown",
		icon:"fas fa-thumbtack",
		labelClass:"in-unknown in-production",
	},
	"pending approval":{
		label:"Approving",
		icon:"fas fa-bullseye",
		labelClass:"in-approving",
	},
	transcoding:{
		label:"Processing",
		icon:"fas fa-file-video",
		labelClass:"in-transcoding",
	},
	transcoding_error:{
		label:"Processing Error",
		icon:"video-plus",
		labelClass:"transcoding-error",
	},
	production_incomplete:{
		label:"Production-Incomplete",
		icon:"errorT",
		labelClass:"production-incomplete",
		tooltipLabel:"Some creative assets are missing or invalid",
	},
	rejected_incomplete:{
		label:"Rejected-Incomplete",
		icon:"errorT",
		labelClass:"rejected-incomplete",
		tooltipLabel:"Missing required assets",
	},
	partner_rejected:{
		label:"Partner Error",
		icon:"rejected",
		labelClass:"in-partner-rejected",
	},
	// CreativePreviews Status Map Values
	not_shared: {
		label: "Not Shared",
		labelClass: "production-incomplete",
	},
	available: {
		label: "Available",
		labelClass: "in-approved",
	},
	expired: {
		label: "Expired",
		labelClass: "in-rejected",
	},
};

export const INCOMPLETE_DVAST_TOOLTIP = "Publish a design with dynamic <br/> components from Ad Studio to <br/> manage versions or approve <br/> creative";
export const INCOMPLETE_IMPOSIUM_TOOLTIP = "Publish a design with dynamic <br/> components from Imposium to <br/> manage versions or approve <br/> creative";

/**
 * Constants for permissioning
 */

export const CV = "Creative View";

export const CREATIVE_VIEW = {
	UPLOAD_AD_FILES:"Upload Ad Files",
	DUPLICATE:"Duplicate",
	RENAME:"Rename",
	SUBMIT_ADS_TO_QA:"Submit Ads to QA",
	APPROVE_ADS: "Approve Ads",
	APPROVE_DVAST_CREATIVES: "Approve DVAST Creatives",
	APPROVE_SOCIAL:"Approve Social Ads",
	RECALL_ADS: "Recall Ads",
	REJECT_ADS: "Reject Ads",
	DOWNLOAD:"Download",
	CREATE_MOCK_UP_DISPLAY:"Create Mock-up Display",
	DELETE:"Delete",
	UPLOAD_VAST:"Upload VAST",
	SHARE_PREVIEW:"Share Preview",
};


/* Num of Click Tags for filter Dictionary */
export const CLICK_TAG_NA_NUM = -1;

export const QATerritories = [{
	label: "UK",
	value: 1,
}, {
	label: "DE",
	value: 2,
}, {
	label: "US",
	value: 3,
}, {
	label: "AU",
	value: 4,
}, {
	label: "NL",
	value: 5,
}];

export const ENABLE_CONCEPT_VERSION_GEN = true;

export const ADSTUDIO_IFRAME_ASSET_DIMENSIONS = {
	[SOCIAL_AD_TYPES.FACEBOOK_IMAGE]: {
		width: 318,
		height: 320,
	},
	[SOCIAL_AD_TYPES.FACEBOOK_IMAGE_MO]: {
		width: 318,
		height: 320,
	},
	[SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO]: {
		width: 318,
		height: 320,
	},
	[SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL]: {
		width: 318,
		height: 320,
	},
	[SOCIAL_AD_TYPES.FACEBOOK_VIDEO]: {
		width: 318,
		height: 320,
	},
	[SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO]: {
		width: 318,
		height: 320,
	},
	[SOCIAL_AD_TYPES.LINKEDIN_CAROUSEL]: {
		width: 540,
		height: 420,
		//TODO make height proportional to width to preserve aspect ratio HOPI-2642
	},
	[SOCIAL_AD_TYPES.LINKEDIN_IMAGE]: {
		width: 540,
		height: 420,
		//TODO make height proportional to width to preserve aspect ratio HOPI-2642

	},
	[SOCIAL_AD_TYPES.LINKEDIN_VIDEO]: {
		width: 540,
		height: 420,
		//TODO make height proportional to width to preserve aspect ratio HOPI-2642
	},
	[SOCIAL_AD_TYPES.PINTEREST_CAROUSEL]: {
		width: 235,
		height: 355,
		//TODO make height proportional to width to preserve aspect ratio HOPI-2642
	},
	[SOCIAL_AD_TYPES.PINTEREST_IMAGE]: {
		width: 235,
		height: 355,
		//TODO make height proportional to width to preserve aspect ratio HOPI-2642
	},
	[SOCIAL_AD_TYPES.PINTEREST_VIDEO]: {
		width: 235,
		height: 355,
		//TODO make height proportional to width to preserve aspect ratio HOPI-2642
	},
	[SOCIAL_AD_TYPES.PINTEREST_MAX_WIDTH_VIDEO]: {
		width: 235,
		height: 355,
		//TODO make height proportional to width to preserve aspect ratio HOPI-2642
	},
	[SOCIAL_AD_TYPES.SNAPCHAT_IMAGE]: {
		width: 315,
		height: 560,
	},
	[SOCIAL_AD_TYPES.SNAPCHAT_VIDEO]: {
		width: 315,
		height: 560,
	},
	[SOCIAL_AD_TYPES.SNAPCHAT_COMMERCIAL]: {
		width: 315,
		height: 560,
	},
	[SOCIAL_AD_TYPES.TIKTOK_VIDEO]: {
		width: 315,
		height: 560,
	},
	[SOCIAL_AD_TYPES.TWITTER_CAROUSEL]: {
		width: 475,
		height: 400,
		//TODO make height proportional to width to preserve aspect ratio HOPI-2642
	},
	[SOCIAL_AD_TYPES.TWITTER_IMAGE]: {
		width: 505,
		height: 400,
		//TODO make height proportional to width to preserve aspect ratio HOPI-2642
	},
	[SOCIAL_AD_TYPES.TWITTER_VIDEO]: {
		width: 505,
		height: 400,
		//TODO make height proportional to width to preserve aspect ratio HOPI-2642
	},
	[SOCIAL_AD_TYPES.YOUTUBE_DISCOVERY]: {
		width: 640,
		height: 360,
	},
	[SOCIAL_AD_TYPES.YOUTUBE_INSTREAM]: {
		width: 640,
		height: 360,
	},
};

export const SUBMIT_QR_MODES = {
	EDIT: "EDIT",
	CREATE: "CREATE",
	DUPLICATE: "DUPLICATE",
};
